import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IoClose } from 'react-icons/io5';

/* ─── Inject scrollbar-hiding CSS once (attribute selector = high specificity) ─── */
if (typeof document !== 'undefined') {
  const ID = '__cm_style__';
  if (!document.getElementById(ID)) {
    const el = document.createElement('style');
    el.id = ID;
    el.textContent = `
      [data-cm-scroll]::-webkit-scrollbar { display: none !important; width: 0 !important; }
      [data-cm-scroll] { -ms-overflow-style: none; scrollbar-width: none; }
    `;
    document.head.appendChild(el);
  }
}

/**
 * CustomModal — portal modal with smooth animation, hidden scrollbar, no white corners.
 */
const CustomModal = ({
  open,
  onClose,
  width = 650,
  closeOnBackdrop = true,
  showClose = true,
  children,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const ANIM_MS = 280;
  const timerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true))
      );
      document.body.style.overflow = 'hidden';
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
      timerRef.current = setTimeout(() => {
        setMounted(false);
        document.body.style.overflow = '';
      }, ANIM_MS);
      return () => clearTimeout(timerRef.current);
    }
  }, [open]);

  const handleKey = useCallback(
    (e) => { if (e.key === 'Escape' && open) onClose(); },
    [open, onClose]
  );
  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  useEffect(() => () => { document.body.style.overflow = ''; }, []);

  if (!mounted) return null;

  /* ─── Inline styles — no Tailwind classes that might be stripped in prod ─── */
  return createPortal(
    /* Backdrop */
    <div
      role="dialog"
      aria-modal="true"
      onClick={closeOnBackdrop ? onClose : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        backgroundColor: `rgba(0,0,0,${visible ? 0.55 : 0})`,
        backdropFilter: visible ? 'blur(6px)' : 'blur(0px)',
        WebkitBackdropFilter: visible ? 'blur(6px)' : 'blur(0px)',
        transition: `background-color ${ANIM_MS}ms ease, backdrop-filter ${ANIM_MS}ms ease`,
      }}
    >
      {/* Panel
        ─ KEY: `contain: paint` is the most reliable way to clip children at
          border-radius edges. Unlike `overflow: hidden`, it works even when a
          child has `overflow-y: auto` (which creates its own GPU layer).
          Reference: https://developer.mozilla.org/en-US/docs/Web/CSS/contain
      */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: `${width}px`,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px',
          backgroundColor: '#ffffff',
          boxShadow: '0 32px 64px -12px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.04)',
          /* ── White-corner fix ── */
          overflow: 'hidden',           /* still needed for semantic stacking */
          contain: 'paint',             /* paint containment: clips ALL children to border-radius */
          /* ── Spring entrance animation ── */
          transform: visible
            ? 'scale(1) translateY(0px)'
            : 'scale(0.93) translateY(20px)',
          opacity: visible ? 1 : 0,
          transition: `transform ${ANIM_MS}ms cubic-bezier(0.34,1.4,0.64,1), opacity ${ANIM_MS}ms ease`,
        }}
      >
        {/* ✕ Close button */}
        {showClose && (
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '14px',
              right: '14px',
              zIndex: 20,
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.18)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'background-color 150ms ease, transform 150ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.32)';
              e.currentTarget.style.transform = 'scale(1.1) rotate(90deg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.18)';
              e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
            }}
          >
            <IoClose size={15} />
          </button>
        )}

        {/* Scrollable body — scrollbar completely hidden via data attribute CSS */}
        <div
          data-cm-scroll=""
          style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
            overscrollBehavior: 'contain',
            /* Firefox & IE fallbacks (attribute CSS handles webkit) */
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CustomModal;
